import chevronDownIcon from '@/assets/icons/chevron-down.svg';
import logoutIcon from '@/assets/icons/logout.svg';
import { paths } from '@/app/routes/paths';
import { SalaamahLogo } from '@/features/auth/components/SalaamahLogo';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { useClickOutside } from '@/shared/hooks/useClickOutside';
import { useEscapeKey } from '@/shared/hooks/useEscapeKey';
import { messages } from '@/shared/i18n';
import { iconMaskStyle } from '@/shared/lib/icon-mask';

import { AppTopBarSearch } from './AppTopBarSearch';
import { DefaultAccountMenu } from './DefaultAccountMenu';
import { GuardianAccountMenu } from './GuardianAccountMenu';
import { NotificationChip } from './NotificationChip';
import { WorkspaceSettingsNavLink } from './WorkspaceSettingsNavLink';
import type { AppTopBarProps } from './types';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { canManageSites, isOrganisationAdmin, useAuthStore } from '@/store';
import { useAppTopBarAccount } from './useAppTopBarAccount';

const tc = messages.common;
const guardianPortalCopy = messages.workspace.guardianDashboard;

/**
 * Primary application header: branding, contextual search, notification placeholder,
 * and an account menu (staff vs guardian-specific).
 */
export function AppTopBar({
  userName,
  logoTo = paths.root,
  avatarUrl,
  variant = 'default',
}: AppTopBarProps) {
  const {
    menuWrapperRef,
    menuOpen,
    setMenuOpen,
    closeMenu,
    confirmOpen,
    isLoggingOut,
    closeLogoutConfirm,
    handleLogoutConfirmed,
    openLogoutConfirm,
    userEmail,
    displayName,
    profileChipLabel,
    isGuardian,
    isGuardianDashboard,
    currentOrgId,
    guardianProviderList,
    showGuardianProviderPicker,
    switchingOrgId,
    handleSelectOrganization,
  } = useAppTopBarAccount({ variant, userName });
  const user = useAuthStore((s) => s.user);
  const isAdmin = isOrganisationAdmin(user);
  // Delegated site management (client 6.1) opens Settings on the Service sites tab only.
  const showOrganisationSettings = !isGuardian && (isAdmin || canManageSites(user));

  useClickOutside(menuWrapperRef, closeMenu, menuOpen);
  useEscapeKey(closeMenu, menuOpen);

  return (
    <header
      className={`relative z-20 flex shrink-0 items-center gap-4 border-b bg-white px-6 pb-4 pt-4 ${
        isGuardian ? 'border-[rgba(88,120,187,0.1)]' : 'border-stroke'
      }`}
    >
      <div className="w-[135px] shrink-0 [&_a]:block">
        <SalaamahLogo to={logoTo} />
      </div>

      <div className="min-w-0 flex-1">
        <AppTopBarSearch variant={variant} isGuardianDashboard={isGuardianDashboard} />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {!isGuardian && showOrganisationSettings ? (
          <WorkspaceSettingsNavLink
            to={isAdmin ? paths.organisationSettings : paths.organisationSettingsServiceSites}
          />
        ) : null}
        <NotificationChip isGuardian={isGuardian} />

        <div className="relative" ref={menuWrapperRef}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={tc.accountMenu}
            onClick={() => {
              setMenuOpen((v) => !v);
            }}
            className={
              isGuardian
                ? 'flex h-[37px] max-w-[240px] items-center gap-1 rounded-[24px] bg-white px-3 py-2 transition-colors hover:bg-brand-50'
                : 'flex min-h-[var(--control-button-height-md)] items-center gap-2 rounded-full bg-white px-3.5 py-2 shadow-card transition-colors hover:bg-brand-50'
            }
          >
            <UserAvatar
              name={profileChipLabel}
              imageAlt={isGuardian ? displayName : undefined}
              avatarUrl={avatarUrl}
              sizeClassName={isGuardian ? 'size-5' : 'size-6'}
              textClassName="text-tiny"
              priority
            />
            <span className="max-w-[200px] truncate text-sm font-semibold text-ink">
              {profileChipLabel}
            </span>
            {isGuardian ? null : (
              <span
                aria-hidden
                className={`block size-3 text-ink transition-transform duration-200 ${
                  menuOpen ? 'rotate-180' : ''
                }`}
                style={iconMaskStyle(chevronDownIcon)}
              />
            )}
          </button>

          {menuOpen ? (
            isGuardian ? (
              <GuardianAccountMenu
                ariaLabel={tc.accountMenu}
                providerSectionTitle={guardianPortalCopy.selectProviderSectionTitle}
                signOutCta={guardianPortalCopy.signOutCta}
                organizations={guardianProviderList}
                showProviderPicker={showGuardianProviderPicker}
                currentOrgId={currentOrgId}
                switchingOrgId={switchingOrgId}
                displayName={displayName}
                userEmail={userEmail}
                avatarUrl={avatarUrl}
                onSelectOrganization={handleSelectOrganization}
                onSignOut={openLogoutConfirm}
              />
            ) : (
              <DefaultAccountMenu
                ariaLabel={tc.accountMenu}
                displayName={displayName}
                userEmail={userEmail}
                avatarUrl={avatarUrl}
                logoutLabel={tc.logout}
                onLogout={openLogoutConfirm}
              />
            )
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={tc.logoutConfirmTitle}
        description={tc.logoutConfirmBody}
        hint={tc.logoutConfirmHint}
        cancelLabel={tc.cancel}
        confirmLabel={isLoggingOut ? tc.loggingOut : tc.logout}
        busy={isLoggingOut}
        confirmIcon={
          <span
            aria-hidden
            className="block size-3.5"
            style={iconMaskStyle(logoutIcon)}
          />
        }
        onCancel={closeLogoutConfirm}
        onConfirm={() => {
          void handleLogoutConfirmed();
        }}
      />
    </header>
  );
}

export type { AppTopBarProps, TopBarVariant } from './types';
